import React, { useState, useContext, useEffect } from 'react';
import { FieldData, TasksRowsFieldType } from 'types/FieldTypes';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import { useAllowedRight } from 'services/permissions/permissionChecks';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useModal } from 'components/modals/ModalContext';
import { enqueueAndDownloadPdf } from 'components/modals/functions/pollPdfRender';
import FormFieldContext from '../FormFieldContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripLines, faLock, faLockOpen, faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { faDownload } from '@fortawesome/pro-solid-svg-icons';
import { JobTaskType, createNewJobTask } from '../../../views/jobs/JobTypes';
import { FormType } from '../../../views/forms/FormTypes';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import { StrictModeDroppable } from 'services/utils/dragDropUtils';
import { v4 as uuidv4 } from 'uuid';
import { renderRowsFieldErrorMessages } from './functions/renderRowsFieldErrorMessages';
import TextareaInput from '../basefields/TextareaInput';
import CheckmarkChecked from '../../../components/forms/fieldsets/checkmarkChecked.svg';
import CheckmarkUnchecked from '../../../components/forms/fieldsets/checkmarkUnchecked.svg';
import Dropdown from '../basefields/Dropdown';
import FormSheetModal from '../../../views/forms/FormSheetModal';
import '../../../style/scss/live-edit.scss';
import '../../../style/scss/forms.scss';

const TasksRowsField: React.FC<TasksRowsFieldType & { data: FieldData }> = ({
    name, jobId, data, dropdownData, helperText, isEditable
}) => {
    const { t } = useTranslation();
    const { editing, setUpdatedData, showErrorAlert } = useContext(FormFieldContext);
    const { initializeModal } = useModal();
    const { activeFeatures, handleLogout } = useAuthContext();
    const { errorMessages } = useGlobalContext();
    const hasRightCheck = useAllowedRight;
    const [rows, setRows] = useState<JobTaskType[]>([]);
    const [forms, setForms] = useState<FormType[]>([]);
    const [userHasOnlyViewRights] = useState<boolean>(hasRightCheck('only_view'));
    const formsEnabled = activeFeatures?.includes('forms_base');
    const [downloadingFormSheetId, setDownloadingFormSheetId] = useState<number | null>(null);

    // If there are tasks included in the fetched data, show them. Otherwise show an empty row
    useEffect(() => {
        if (data && data[name] && data[name].length > 0) {
            setRows(data[name])
        } else {
            const firstPosition = 1
            const newRow = createNewJobTask(uuidv4(), firstPosition, jobId);
            setRows([newRow]);
        }
    }, [data, jobId]);

    // Set the forms from the dropdown data
    useEffect(() => {
        setForms(dropdownData?.form?.results || []);
    }, [dropdownData])

    // Update the rows state and updated data
    const syncUpdatedData = (updatedRows: JobTaskType[]) => {
        setRows(updatedRows);
        setUpdatedData((current: any) => ({
            ...current,
            [name]: updatedRows,
        }));
    };

    // Update a row
    const updateRow = (identifier: string, updater: (row: JobTaskType) => JobTaskType) => {
        const updated = rows.map(row =>
            row.id?.toString() === identifier || row.uuid === identifier
                ? updater(row)
                : row
        );
        syncUpdatedData(updated);
    };

    // Handle form change
    const handleFormChange = (identifier: string, value: string) => {
        const formId = value === 'none' ? null : Number(value);
        const selectedForm = forms.find(form => form.id === formId);

        updateRow(identifier, row => ({
            ...row,
            form_id: formId,
            form_sheet: formId
                ? {
                    id: row.form_sheet?.id || null,
                    form_id: formId,
                    form_name: selectedForm?.name || '',
                    status: row.form_sheet?.status || 'draft',
                }
                : null,
        }));
    };
    
    // Handle the creation of a new row
    const handleAddRow = () => {
        // Split rows in non deleted and deleted rows
        const nonDeletedRows = rows.filter(row => !row.deleted);
        const deletedRows = rows.filter(row => row.deleted);

        // Determine the position of the new row and create it
        const newPosition = nonDeletedRows.length + 1;
        const newRow = createNewJobTask(uuidv4(), newPosition, jobId);

        // Add the new row at the end of the list with non deleted rows and add the deleted rows
        const updatedNonDeletedItems = [...nonDeletedRows, newRow];
        const updatedRows = [...updatedNonDeletedItems, ...deletedRows];
    
        syncUpdatedData(updatedRows);
    };

    // Handle deletion of a row
    const handleDeleteRow = (identifier: string) => {
        const updatedRows = [...rows];
        const rowIndex = updatedRows.findIndex(row => row.id?.toString() === identifier || row.uuid === identifier);

        if (rowIndex !== -1) {
            // Mark the row as deleted without actually deleting it
            updatedRows[rowIndex].deleted = true;
    
            // Update the ordering of the rows which are not marked as deleted
            const nonDeletedRows = updatedRows.filter(row => !row.deleted);
            const reorderedNonDeletedRows = nonDeletedRows.map((row, index) => ({
                ...row,
                position: index + 1,
            }));

            // Add the deleted rows to the reordered list to keep them for the updated data
            const finalItems = [...reorderedNonDeletedRows, ...updatedRows.filter(row => row.deleted)];

            syncUpdatedData(finalItems);
        } 
    };

    // Handle ordering change
    const handleOrderingChange = (result: any) => {
        if (!result.destination) return;
        const { source, destination } = result;
        
        // Only use the non deleted rows to determine the new ordering
        const nonDeletedRows = rows.filter(row => !row.deleted);
        const [reorderedRow] = nonDeletedRows.splice(source.index, 1);
        nonDeletedRows.splice(destination.index, 0, reorderedRow);
    
        // Determine the ordering for the non deleted rows
        const updatedNonDeletedRows = nonDeletedRows.map((item, index) => ({
            ...item,
            ordering: index + 1,
        }));
    
        // Add the deleted rows to the list without changing their ordering
        const finalItems = [...updatedNonDeletedRows, ...rows.filter(row => row.deleted)];

        syncUpdatedData(finalItems);
    };

    // Download the formsheet pdf
    const handleDownloadPdf = async (formSheetId: number, formName: string, event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();

        // Set the download formsheet id
        setDownloadingFormSheetId(formSheetId);

        try {
            // Set the document name
            const documentName = formName || t('form_sheet.general.object_name.singular') || 'Form Sheet';
            const safeDocumentName = documentName.replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, '_');

            // Start the pdf render on the worker, poll until finished and download it
            await enqueueAndDownloadPdf({
                enqueueUrl: `generate_form_sheet_pdf/${formSheetId}`,
                fileName: `${safeDocumentName}_${formSheetId}.pdf`,
                handleLogout,
            });
        } catch (error) {
            console.error('Error downloading form sheet PDF:', error);
        } finally {
            setDownloadingFormSheetId(null);
        }
    };

    // Render the form sheet download button
    const renderFormSheetDownloadButton = (row: JobTaskType) => {
        const formSheetId = row.form_sheet?.id || null;
        const formName = row.form_sheet?.form_name || '';

        if (!formSheetId) {
            return null;
        }

        const isDownloading = downloadingFormSheetId === formSheetId;

        return (
            <span
                className={`form-sheet-download-icon tooltip-icon ${isDownloading ? 'loading' : ''}`}
                onClick={(event) => {
                    if (!isDownloading) {
                        handleDownloadPdf(formSheetId, formName, event);
                    }
                }}
            >
                {isDownloading ? (
                    <span className="form-sheet-icon-loader" />
                ) : (
                    <FontAwesomeIcon icon={faDownload} />
                )}

                {!isDownloading && (
                    <span className='tooltip'>
                        {t('form_sheet.general.download_pdf_button_label')}
                    </span>
                )}
            </span>
        );
    };

    // Render the task text
    const renderTaskText = (row: JobTaskType) => {
        const formSheetId = row.form_sheet?.id || null;
        const formName = row.form_sheet?.form_name || '';
        const description = row.description?.trim() || '';
        const hasDescription = description !== '';

        // Open the form sheet modal
        const openFormSheetModal = (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();

            if (formSheetId) {
                initializeModal(
                    <FormSheetModal
                        formSheetId={formSheetId}
                        jobResolution={data?.resolution}
                        taskDescription={description}
                    />, { 
                        modalSize: 'medium-large',
                        modalPosition: 'center',
                    }
                );
            }
        };

        // Show empty value
        if (description === '' && !formName) {
            return (
                <span className="text">
                    <span className="empty-value">Voeg taakomschrijving toe</span>
                </span>
            );
        }

        return (
            <span className="text">
                {hasDescription && (
                    description
                )}
                {hasDescription && formName && formSheetId && (
                    ' - '
                )}
                {formName && formSheetId && (
                    <span className="form-sheet-actions">
                        <span className="tooltip-icon form-sheet-link">
                            <span
                                className="item-link"
                                onClick={openFormSheetModal}
                            >
                                {formName}
                            </span>
                            <span className="tooltip">
                                Bekijk formulier
                            </span>
                        </span>

                        {renderFormSheetDownloadButton(row)}
                    </span>
                )}
            </span>
        );
    };

    return (
        <div className='rows-field'>
            {showErrorAlert &&
                <div className="alert form-alert alert-danger" role="alert">
                    {t(errorMessages.general, { defaultValue: errorMessages.general })}
                </div>
            }
            {editing ? (
                // Edit mode
                <div className='edit-mode'>
                    <DragDropContext onDragEnd={handleOrderingChange}>
                        <StrictModeDroppable droppableId="droppable">
                            {(provided) => (
                                <div {...provided.droppableProps} ref={provided.innerRef}>
                                    {rows && rows.filter(row => row.deleted === false).map((row, index) => {
                                        const identifier = row.id?.toString() || row.uuid;
                                        return (
                                            <Draggable key={identifier} draggableId={`item-${identifier}`} index={index} isDragDisabled={rows.length <= 1}>
                                                {(provided) => {
                                                    return (
                                                        <div className='list-item draggable' ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                                            <div className={`tasks-row ${formsEnabled ? 'forms-enabled' : ''}`}>
                                                                <div 
                                                                    className="task-check tooltip-icon"
                                                                    onClick={() => {
                                                                        if (!identifier) return;
                                                                        updateRow(identifier, row => ({
                                                                            ...row,
                                                                            checked: !row.checked,
                                                                        }))
                                                                    }}
                                                                >
                                                                    {row.checked
                                                                        ? <img src={CheckmarkChecked} width={24} height={24} />
                                                                        : <img src={CheckmarkUnchecked} width={24} height={24} />
                                                                    }
                                                                    {row.checked
                                                                        ? <span className="tooltip">{t('job.tasks.uncheck')}</span>
                                                                        : <span className="tooltip">{t('job.tasks.check')}</span>
                                                                    }
                                                                </div>
                                                                <TextareaInput
                                                                    key={row.uuid}
                                                                    name='description'
                                                                    value={row.description}
                                                                    rows={1}
                                                                    placeholder={t('job.tasks.description_placeholder')}
                                                                    onChange={(event: string | React.ChangeEvent<HTMLTextAreaElement>) => {
                                                                        if (!identifier) return;
                                                                        if (typeof event !== "string") {
                                                                            updateRow(identifier, row => ({
                                                                                ...row,
                                                                                description: event.target.value,
                                                                            }))
                                                                        }
                                                                    }}
                                                                />
                                                                {formsEnabled && (
                                                                    <div className='task-form-selector'>
                                                                        <Dropdown<FormType>
                                                                            name={`task_form_${identifier}`}
                                                                            options={forms}
                                                                            selectedOption={forms.find(form => String(form.id) === String(row.form_id || row.form_sheet?.form_id)) ?? null}
                                                                            backendField='id'
                                                                            optionFormat='name'
                                                                            disabled_selected='job.tasks.select_form_disabled_selected'
                                                                            selectionFormat={(option) => option.name}
                                                                            allowNoneOption={true}
                                                                            noneOptionLabel='forms.none_option'
                                                                            showSearch={true}
                                                                            onChange={(value) => {
                                                                                if (identifier) {
                                                                                    handleFormChange(identifier, value)
                                                                                }
                                                                            }}
                                                                        />
                                                                    </div>
                                                                )}
                                                                <div
                                                                    className={`task-required-icon ${row.required ? 'active' : ''} tooltip-icon`}
                                                                    onClick={() => {
                                                                        if (!identifier) return;
                                                                            updateRow(identifier, r => ({
                                                                                ...row,
                                                                                required: !row.required,
                                                                            }))
                                                                        }
                                                                    }
                                                                >
                                                                    {row.required
                                                                        ? <FontAwesomeIcon icon={faLock} />
                                                                        : <FontAwesomeIcon icon={faLockOpen} />
                                                                    }
                                                                    {row.required
                                                                        ? <span className="tooltip">{t('job.tasks.task_required_tooltip')}</span>
                                                                        : <span className="tooltip">{t('job.tasks.task_required_tooltip')}</span>
                                                                    }
                                                                </div>
                                                                <div className='delete-icon tooltip-icon'>
                                                                    <FontAwesomeIcon 
                                                                        icon={faTrash} 
                                                                        onClick={() => handleDeleteRow(row.id?.toString() || row.uuid || '')} />
                                                                    <span className="tooltip">{t('general.delete')}</span>
                                                                </div>
                                                                <div className={`order-icon tooltip-icon ${rows.length === 1 ? 'visibility-hidden' : ''}`}>
                                                                    <FontAwesomeIcon icon={faGripLines} />
                                                                    <span className="tooltip">{t('general.reorder')}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )
                                                }}
                                            </Draggable>
                                        )
                                    })}
                                    {provided.placeholder}
                                </div>
                            )}
                        </StrictModeDroppable>
                    </DragDropContext>
                    {errorMessages && 
                        <div className='error-message'>
                            {renderRowsFieldErrorMessages(errorMessages, 'tasks', t)}
                        </div>
                    }
                    <div onClick={(e) => {e.preventDefault(); handleAddRow(); }} 
                        className="add-new-button">
                        {t('job.tasks.add_new_item')}
                    </div>    
                    {helperText && 
                        <div className="helper-text">
                            {t(helperText)}
                        </div>
                    }
                </div>
            ) : (
                // View mode
                <div className={`full-width-alignment ${isEditable?.editability && !userHasOnlyViewRights ? 'editable' : ''}`}>
                    <div className="view-mode">
                        <span className='p'>
                            <div className='item-list'>
                                {rows.filter(row => row.id).map((row) => {                                    
                                    return (
                                        <div key={row.id!.toString()} className='item task-item'>
                                            <span className='task-check'>
                                                {row.checked
                                                    ? <img src={CheckmarkChecked} />
                                                    : <img src={CheckmarkUnchecked} />
                                                }
                                            </span>
                                            {renderTaskText(row)}
                                        </div>
                                    )
                                })}
                                {isEditable?.editability && !userHasOnlyViewRights && (
                                    // Only show add item label when the object is editable
                                    <div className='add-item'>
                                        {t('job.tasks.add_item')}
                                    </div>
                                )}
                            </div>
                        </span>
                        <span className='edit-icon'>
                            <FontAwesomeIcon icon={faPen} />
                        </span> 
                    </div>
                </div>
            )}
        </div>
    );
}

export default TasksRowsField;